import { useEffect, useRef, useState } from "react";

/**
 * Provider-agnostic incoming-call contract.
 *
 * The Call Panel (formerly CallDock) subscribes to this hook. v1 ships a
 * dev-only mock source behind the `VITE_CALL_DOCK` flag so design +
 * workflow can be exercised today without a real telephony provider.
 * When we wire up Twilio / RingCentral / 8x8 / etc., we swap the
 * source inside this file — the panel and the rest of the app don't
 * change.
 */

export type CallStatus = "idle" | "search" | "ringing" | "active" | "intake" | "wrapup" | "ended";

/** One finalized subtitle line from the live transcript (server `call:transcript` SSE). */
export interface TranscriptLine {
    /** Monotonic per-call sequence — orders lines and de-dupes re-delivered SSE. */
    seq: number;
    /** Who spoke. Phase 1 only streams the caller leg; "office" arrives in Phase 2. */
    speaker: "caller" | "office";
    text: string;
    ts: number;
}

/** Live job draft produced by the AI copilot (server `call:draft` SSE). */
export interface CallAiDraft {
    customerName: string | null;
    phone: string | null;
    addressLine: string | null;
    city: string | null;
    region: string | null;
    postalCode: string | null;
    serviceType: string | null;
    urgency: "routine" | "soon" | "emergency" | null;
    summary: string | null;
}

export interface IncomingCall {
    id: string;
    /** E.164 if we have it, otherwise raw caller-id string. */
    fromNumber: string;
    /** Optional vendor-supplied caller name (carrier CNAM, voicemail, etc.). */
    fromName?: string;
    /** Optional matched customer hint from the telephony layer. */
    matchedCustomerId?: string;
    /** Twilio CallSid of the caller's leg — drives hold/hand-off in conference mode. */
    callSid?: string;
    receivedAt: number;
    status: CallStatus;
    /**
     * Where the session came from. `telephony` = a real (or mock) inbound
     * call from the CallBus; `walkup` = the office opened the panel manually
     * (top-bar button / Cmd+J) to handle a walk-in or outbound call. Walk-up
     * sessions start in `search` and never show the minimized ringing pill.
     * Treated as `telephony` when absent for backward compatibility.
     */
    origin?: "telephony" | "walkup";
    /** Free-form notes captured during the call. */
    notes?: string;
    /** Live AI copilot job draft (updated as the call is transcribed). */
    aiDraft?: CallAiDraft;
    /** Live subtitles: finalized transcript lines, oldest→newest (capped). */
    transcript?: TranscriptLine[];
    /** This is a practice call against the AI Training Simulator, not a real caller. */
    training?: boolean;
    /** ID of a job created during this call's intake phase. */
    createdJobId?: string;
    /** Epoch ms when the call was accepted (status → active). */
    callStartedAt?: number;
    /** Epoch ms when the call was ended (status → wrapup). */
    callEndedAt?: number;
}

interface CallActions {
    /** Mark the current call as answered (still keeps it in dock as "active"). */
    accept: () => void;
    /** Hang up / dismiss without taking action. */
    decline: () => void;
    /** Hide the panel entirely — caller goes to voicemail / handled elsewhere. */
    end: () => void;
    /** Transition to the inline job intake form. */
    startIntake: () => void;
    /** Transition to post-call wrap-up summary. */
    startWrapup: () => void;
    /** Update call notes (persisted in the bus). */
    updateNotes: (notes: string) => void;
    /** Attach a created job ID to the call. */
    setCreatedJobId: (id: string) => void;
    /** Update the matched customer on the fly (e.g. from search). */
    setMatchedCustomer: (id: string) => void;
    /**
     * Dev / demo helper: simulate an inbound call. No-op in production
     * builds and when `VITE_CALL_DOCK` is not set.
     */
    triggerMock: (overrides?: Partial<IncomingCall>) => void;
}

const STORAGE_KEY = "ironclad-mock-call-v1";
const FLAG_ENABLED = (import.meta.env.VITE_CALL_DOCK ?? "") !== "" || import.meta.env.DEV;

type Listener = (call: IncomingCall | null) => void;

/**
 * Singleton in-memory bus + localStorage relay so a call fired from
 * the devtools console reaches every mounted dock instance, including
 * cross-tab. Keeps the hook side-effect-free past first import.
 */
class CallBus {
    private listeners = new Set<Listener>();
    private current: IncomingCall | null = null;

    constructor() {
        if (typeof window === "undefined") return;
        window.addEventListener("storage", (e) => {
            if (e.key !== STORAGE_KEY) return;
            try {
                const next = e.newValue ? (JSON.parse(e.newValue) as IncomingCall) : null;
                this.current = next;
                this.emit();
            } catch {
                // ignore malformed payloads
            }
        });
    }

    subscribe(fn: Listener) {
        this.listeners.add(fn);
        fn(this.current);
        return () => {
            this.listeners.delete(fn);
        };
    }

    get() {
        return this.current;
    }

    set(call: IncomingCall | null) {
        this.current = call;
        try {
            if (call) {
                window.localStorage.setItem(STORAGE_KEY, JSON.stringify(call));
            } else {
                window.localStorage.removeItem(STORAGE_KEY);
            }
        } catch {
            // localStorage unavailable; in-memory bus still works for this tab
        }
        this.emit();
    }

    /**
     * Set the current session WITHOUT persisting to localStorage, so it stays
     * tab-local and never syncs cross-tab. Used for manual "walk-up" sessions
     * (the office opening the panel by hand) — a walk-up in one tab shouldn't
     * pop the panel open in every other tab the way a real inbound call does.
     */
    setEphemeral(call: IncomingCall | null) {
        this.current = call;
        this.emit();
    }

    patch(updates: Partial<IncomingCall>) {
        if (!this.current) return;
        this.set({ ...this.current, ...updates });
    }

    private emit() {
        for (const fn of this.listeners) fn(this.current);
    }
}

const bus = typeof window === "undefined" ? null : new CallBus();

if (FLAG_ENABLED && typeof window !== "undefined") {
    (window as unknown as { triggerMockCall?: (overrides?: Partial<IncomingCall>) => void }).triggerMockCall = (
        overrides,
    ) => {
        const sample: IncomingCall = {
            id: `mock-${Date.now()}`,
            fromNumber: "+15125550199",
            fromName: "Unknown caller",
            receivedAt: Date.now(),
            status: "ringing",
            origin: "telephony",
            ...overrides,
        };
        bus?.set(sample);
    };
}

/**
 * Imperative session controls used by the CallPanelProvider so any screen can
 * drive the panel without coupling to the bus internals.
 */

/** Open a manual walk-up session (top-bar button / Cmd+J). Tab-local. */
export function openWalkupSession() {
    bus?.setEphemeral({
        id: `walkup-${Date.now()}`,
        fromNumber: "",
        receivedAt: Date.now(),
        status: "search",
        origin: "walkup",
    });
}

/** Open a telephony session (real or mock inbound call). Syncs cross-tab. */
export function openTelephonyCall(call: IncomingCall) {
    bus?.set({ ...call, origin: "telephony" });
}

/**
 * Open an outbound telephony session (click-to-dial). Shows the callee and an
 * active-call timer while the softphone places the call.
 */
export function openOutboundCall(opts: { toNumber: string; matchedCustomerId?: string; fromName?: string }) {
    bus?.set({
        id: `out-${Date.now()}`,
        fromNumber: opts.toNumber,
        fromName: opts.fromName,
        matchedCustomerId: opts.matchedCustomerId,
        receivedAt: Date.now(),
        status: "active",
        origin: "telephony",
        callStartedAt: Date.now(),
    });
}

/** Dismiss whatever session is currently open. */
export function clearCallSession() {
    bus?.set(null);
}

/** Merge an AI copilot draft into the current session (from the `call:draft` SSE). */
export function applyCallDraft(draft: CallAiDraft) {
    if (!bus?.get()) return;
    bus.patch({ aiDraft: draft });
}

/** Keep the transcript bounded — a long call shouldn't grow the bus payload without limit. */
const MAX_TRANSCRIPT_LINES = 60;

/**
 * Append one live-subtitle line to the current session (from the `call:transcript`
 * SSE). Ignored when a different call is being shown (callSid mismatch), and
 * de-duped by seq so a re-delivered SSE frame never doubles a line.
 */
export function appendTranscriptLine(line: TranscriptLine, callSid?: string) {
    const cur = bus?.get();
    if (!cur) return;
    // Guard against a stale line bleeding into the next call: if both the session
    // and the event carry a callSid and they differ, drop it.
    if (callSid && cur.callSid && callSid !== cur.callSid) return;
    const existing = cur.transcript ?? [];
    if (existing.some((l) => l.seq === line.seq)) return;
    const next = [...existing, line].slice(-MAX_TRANSCRIPT_LINES);
    bus?.patch({ transcript: next });
}

/** Last-10-digit comparison so "+15125550199" matches "(512) 555-0199". */
function sameCallerNumber(a: string, b: string): boolean {
    const da = (a ?? "").replace(/\D/g, "").slice(-10);
    const db = (b ?? "").replace(/\D/g, "").slice(-10);
    return da.length >= 7 && da === db;
}

/**
 * Resolve an open, never-answered telephony session as missed (server
 * `call:missed` SSE). If the current panel is showing this caller and the
 * office never picked it up (no `callStartedAt`), close it out so the panel
 * doesn't hang open on a call nobody took. A call that was answered is left
 * alone (the server only fires `call:missed` for unanswered legs anyway).
 */
export function resolveMissedCall(fromNumber: string) {
    const cur = bus?.get();
    if (!cur || cur.origin !== "telephony" || cur.callStartedAt) return;
    if (!sameCallerNumber(cur.fromNumber, fromNumber)) return;
    bus?.patch({ status: "ended", callEndedAt: Date.now() });
    setTimeout(() => {
        const s = bus?.get();
        if (s && s.id === cur.id) bus?.set(null);
    }, 1500);
}

/**
 * React hook used by the Call Panel. Returns the active call (if any)
 * and actions to drive its lifecycle.
 */
export function useIncomingCall(): { call: IncomingCall | null } & CallActions {
    const [call, setCall] = useState<IncomingCall | null>(null);
    const callRef = useRef(call);
    callRef.current = call;

    useEffect(() => {
        if (!bus) return;
        return bus.subscribe(setCall);
    }, []);

    const accept = () => {
        if (!callRef.current) return;
        bus?.patch({ status: "active", callStartedAt: Date.now() });
    };

    const decline = () => {
        if (!callRef.current) return;
        bus?.patch({ status: "ended", callEndedAt: Date.now() });
        setTimeout(() => bus?.set(null), 600);
    };

    const end = () => bus?.set(null);

    const startIntake = () => {
        if (!callRef.current) return;
        bus?.patch({ status: "intake" });
    };

    const startWrapup = () => {
        if (!callRef.current) return;
        bus?.patch({ status: "wrapup", callEndedAt: Date.now() });
    };

    const updateNotes = (notes: string) => {
        bus?.patch({ notes });
    };

    const setCreatedJobId = (id: string) => {
        bus?.patch({ createdJobId: id });
    };

    const setMatchedCustomer = (id: string) => {
        bus?.patch({ matchedCustomerId: id });
    };

    const triggerMock = (overrides?: Partial<IncomingCall>) => {
        if (!FLAG_ENABLED) return;
        const sample: IncomingCall = {
            id: `mock-${Date.now()}`,
            fromNumber: "+15125550199",
            fromName: "Unknown caller",
            receivedAt: Date.now(),
            status: "ringing",
            ...overrides,
        };
        bus?.set(sample);
    };

    return {
        call,
        accept,
        decline,
        end,
        startIntake,
        startWrapup,
        updateNotes,
        setCreatedJobId,
        setMatchedCustomer,
        triggerMock,
    };
}

export const CALL_DOCK_ENABLED = FLAG_ENABLED;
